

import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL;
const token = localStorage.getItem('accessToken');

export default function GovHazmatReleaseSection({ caseId, sofaResponse }) {
  const [releases, setReleases] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [newRelease, setNewRelease] = useState({
    site_name: '',
    street1: '',
    street2: '',
    city: '',
    state: '',
    zip_code: '',
    government_unit: '',
    gov_unit_city: '',
    gov_unit_state: '',
    gov_unit_zip: '',
    environmental_law: '',
    notice_date: ''
  });
  const [editRelease, setEditRelease] = useState(null);

  // Fetch list
  useEffect(() => {
    if (!sofaResponse) return;
    (async () => {
      try {
        const res = await fetch(
          `${API_URL}/cases/${caseId}/sofa-gov-hazmat-releases/?sofa_response=${sofaResponse.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error('Failed to fetch releases');
        setReleases(await res.json());
      } catch (err) {
        console.error(err);
      }
    })();
  }, [caseId, sofaResponse]);

  // Open modals
  function openAddModal() {
    setNewRelease({
      site_name: '',
      street1: '',
      street2: '',
      city: '',
      state: '',
      zip_code: '',
      government_unit: '',
      gov_unit_city: '',
      gov_unit_state: '',
      gov_unit_zip: '',
      environmental_law: '',
      notice_date: ''
    });
    setModalOpen(true);
  }
  function openEditModal(item) {
    setEditRelease({ ...item });
    setEditModalOpen(true);
  }

  // Create
  async function handleSaveNew() {
    try {
      const res = await fetch(
        `${API_URL}/cases/${caseId}/sofa-gov-hazmat-releases/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(newRelease)
        }
      );
      if (!res.ok) throw new Error('Failed to add release');
      const created = await res.json();
      setReleases(prev => [...prev, created]);
      setModalOpen(false);
    } catch (err) {
      console.error(err);
    }
  }

  // Update
  async function handleSaveEdit() {
    try {
      const res = await fetch(
        `${API_URL}/cases/${caseId}/sofa-gov-hazmat-releases/${editRelease.id}/`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(editRelease)
        }
      );
      if (!res.ok) throw new Error('Failed to update release');
      const updated = await res.json();
      setReleases(prev =>
        prev.map(r => (r.id === updated.id ? updated : r))
      );
      setEditModalOpen(false);
    } catch (err) {
      console.error(err);
    }
  }

  // Delete
  async function handleDelete(id) {
    try {
      const res = await fetch(
        `${API_URL}/cases/${caseId}/sofa-gov-hazmat-releases/${id}/`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      if (!res.ok) throw new Error('Failed to delete release');
      setReleases(prev => prev.filter(r => r.id !== id));
    } catch (err) {
      console.error(err);
    }
  }

  return (
    <section className="p-6 bg-white shadow-lg rounded mb-6">
      <h2 className="text-xl font-semibold mb-4">
        Question 25: Hazardous Material Release Notices
      </h2>
      <p className="text-sm text-gray-600 mb-4">
            List notices to a government that you have released hazerdous material.
        </p>
      {releases.length === 0 ? (
        <p>No hazardous release notices recorded.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-4">
          {releases.map(item => (
            <div
              key={item.id}
              className="relative p-4 bg-white shadow rounded cursor-pointer hover:shadow-md transition"
              onClick={() => openEditModal(item)}
            >
              <button
                onClick={e => {
                  e.stopPropagation();
                  handleDelete(item.id);
                }}
                className="absolute top-2 right-2 text-red-600 hover:text-red-800"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" clipRule="evenodd"
                        d="M6 2a1 1 0 00-1 1v1H3a1 1 0 000
                           2h14a1 1 0 000-2h-2V3a1 1 0 00-1-1H6zM4
                           6v10a2 2 0 002 2h8a2 2 0 002-2V6H4zm3
                           2a1 1 0 000 2v6a1 1 0 102 0V8a1 1 0 00-2
                           0zm4 0a1 1 0 000 2v6a1 1 0 102 0V8a1 1 0
                           00-2 0z"/>
                </svg>
              </button>
              <h3 className="font-medium text-lg">{item.site_name}</h3>
              <p className="mt-1 text-sm">
                {item.street1}{item.street2 ? `, ${item.street2}` : ''}, {item.city}, {item.state} {item.zip_code}
              </p>
              <p className="mt-2 text-sm">
                <strong>Gov Unit:</strong> {item.government_unit} — {item.gov_unit_city}, {item.gov_unit_state} {item.gov_unit_zip}
              </p>
              <p className="mt-2 text-sm"><strong>Law:</strong> {item.environmental_law}</p>
              <p className="mt-2 text-sm"><strong>Notice Date:</strong> {item.notice_date || '—'}</p>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={openAddModal}
        className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
      >
        Add Release Notice
      </button>

      {/* Add Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-full max-w-lg overflow-y-auto max-h-[90vh]">
            <h3 className="text-lg font-semibold mb-4">Add Hazardous Release Notice</h3>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Site Name</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={newRelease.site_name}
                  onChange={e => setNewRelease(prev => ({ ...prev, site_name: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Street 1</label>
                  <input
                    type="text"
                    className="w-full border rounded px-2 py-1"
                    value={newRelease.street1}
                    onChange={e => setNewRelease(prev => ({ ...prev, street1: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Street 2</label>
                  <input
                    type="text"
                    className="w-full border rounded px-2 py-1"
                    value={newRelease.street2}
                    onChange={e => setNewRelease(prev => ({ ...prev, street2: e.target.value }))}
                  />
                </div>
              </div>
              <div className="grid grid-cols-7 gap-4">
                <div className="col-span-4">
                  <label className="block text-sm font-medium mb-1">City</label>
                  <input
                    type="text"
                    className="w-full border rounded px-2 py-1"
                    value={newRelease.city}
                    onChange={e => setNewRelease(prev => ({ ...prev, city: e.target.value }))}
                  />
                </div>
                <div className="col-span-1">
                  <label className="block text-sm font-medium mb-1">State</label>
                  <input
                    type="text"
                    className="w-full border rounded px-2 py-1"
                    value={newRelease.state}
                    onChange={e => setNewRelease(prev => ({ ...prev, state: e.target.value }))}
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Zip</label>
                  <input
                    type="text"
                    className="w-full border rounded px-2 py-1"
                    value={newRelease.zip_code}
                    onChange={e => setNewRelease(prev => ({ ...prev, zip_code: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Government Unit</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={newRelease.government_unit}
                  onChange={e => setNewRelease(prev => ({ ...prev, government_unit: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-7 gap-4">
                <div className="col-span-4">
                  <label className="block text-sm font-medium mb-1">Gov Unit City</label>
                  <input
                    type="text"
                    className="w-full border rounded px-2 py-1"
                    value={newRelease.gov_unit_city}
                    onChange={e => setNewRelease(prev => ({ ...prev, gov_unit_city: e.target.value }))}
                  />
                </div>
                <div className="col-span-1">
                  <label className="block text-sm font-medium mb-1">State</label>
                  <input
                    type="text"
                    className="w-full border rounded px-2 py-1"
                    value={newRelease.gov_unit_state}
                    onChange={e => setNewRelease(prev => ({ ...prev, gov_unit_state: e.target.value }))}
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Gov Unit Zip</label>
                  <input
                    type="text"
                    className="w-full border rounded px-2 py-1"
                    value={newRelease.gov_unit_zip}
                    onChange={e => setNewRelease(prev => ({ ...prev, gov_unit_zip: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Environmental Law</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={newRelease.environmental_law}
                  onChange={e => setNewRelease(prev => ({ ...prev, environmental_law: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Notice Date</label>
                <input
                  type="date"
                  className="w-full border rounded px-2 py-1"
                  value={newRelease.notice_date}
                  onChange={e => setNewRelease(prev => ({ ...prev, notice_date: e.target.value }))}
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2 mt-4">
              <button onClick={() => setModalOpen(false)} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
              <button onClick={handleSaveNew} className="px-4 py-2 bg-blue-600 text-white rounded">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModalOpen && editRelease && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-full max-w-lg overflow-y-auto max-h-[90vh]">
            <h3 className="text-lg font-semibold mb-4">Edit Hazardous Release Notice</h3>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Site Name</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={editRelease.site_name}
                  onChange={e => setEditRelease(prev => ({ ...prev, site_name: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Street 1</label>
                  <input
                    type="text"
                    className="w-full border rounded px-2 py-1"
                    value={editRelease.street1}
                    onChange={e => setEditRelease(prev => ({ ...prev, street1: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Street 2</label>
                  <input
                    type="text"
                    className="w-full border rounded px-2 py-1"
                    value={editRelease.street2}
                    onChange={e => setEditRelease(prev => ({ ...prev, street2: e.target.value }))}
                  />
                </div>
              </div>
              <div className="grid grid-cols-7 gap-4">
                <div className="col-span-4">
                  <label className="block text-sm font-medium mb-1">City</label>
                  <input
                    type="text"
                    className="w-full border rounded px-2 py-1"
                    value={editRelease.city}
                    onChange={e => setEditRelease(prev => ({ ...prev, city: e.target.value }))}
                  />
                </div>
                <div className="col-span-1">
                  <label className="block text-sm font-medium mb-1">State</label>
                  <input
                    type="text"
                    className="w-full border rounded px-2 py-1"
                    value={editRelease.state}
                    onChange={e => setEditRelease(prev => ({ ...prev, state: e.target.value }))}
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Zip</label>
                  <input
                    type="text"
                    className="w-full border rounded px-2 py-1"
                    value={editRelease.zip_code}
                    onChange={e => setEditRelease(prev => ({ ...prev, zip_code: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Government Unit</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={editRelease.government_unit}
                  onChange={e => setEditRelease(prev => ({ ...prev, government_unit: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-7 gap-4">
                <div className="col-span-4">
                  <label className="block text-sm font-medium mb-1">Gov Unit City</label>
                  <input
                    type="text"
                    className="w-full border rounded px-2 py-1"
                    value={editRelease.gov_unit_city}
                    onChange={e => setEditRelease(prev => ({ ...prev, gov_unit_city: e.target.value }))}
                  />
                </div>
                <div className="col-span-1">
                  <label className="block text-sm font-medium mb-1">Gov Unit State</label>
                  <input
                    type="text"
                    className="w-full border rounded px-2 py-1"
                    value={editRelease.gov_unit_state}
                    onChange={e => setEditRelease(prev => ({ ...prev, gov_unit_state: e.target.value }))}
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Gov Unit Zip</label>
                  <input
                    type="text"
                    className="w-full border rounded px-2 py-1"
                    value={editRelease.gov_unit_zip}
                    onChange={e => setEditRelease(prev => ({ ...prev, gov_unit_zip: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Environmental Law</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={editRelease.environmental_law}
                  onChange={e => setEditRelease(prev => ({ ...prev, environmental_law: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Notice Date</label>
                <input
                  type="date"
                  className="w-full border rounded px-2 py-1"
                  value={editRelease.notice_date}
                  onChange={e => setEditRelease(prev => ({ ...prev, notice_date: e.target.value }))}
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2 mt-4">
              <button onClick={() => setEditModalOpen(false)} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
              <button onClick={handleSaveEdit} className="px-4 py-2 bg-blue-600 text-white rounded">Save</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}